import type {Config,Parameter} from "./attenuation-contract";

export const dilutionId="solution-preparation-dilution";
export const spectroId="spectrophotometer";
export const chemistryIds=[dilutionId,spectroId] as const;

const n=(label:string,unit:string,value:number,min:number,max:number,step?:number):Parameter=>({
  type:"number",label,unit,default:value,minimum:min,maximum:max,...(step?{step}:{})
});

export const chemistrySchemas:Record<string,Record<string,Parameter>>={
  [dilutionId]:{
    allowModeSwitch:{type:'boolean',label:'Autoriser les élèves à changer de mode',default:false},
    showConcentrations:{type:'boolean',label:'Afficher les concentrations',default:true},
    samplingDevice:{type:'string',label:'Instrument de prélèvement',default:'burette',enum:['burette','pipette']},
    samplingVolumeMl:n('Volume proposé au prélèvement','mL',15,0.01,1000),
    pipetteVolumeMl:n('Volume de la pipette jaugée','mL',5,0.1,100),
    mode:{type:'string',label:'Mode',default:'guided',enum:['method','guided','practice']},
    showInstructionPanel:{type:'boolean',label:'Afficher les consignes',default:true},
    showStepCounter:{type:'boolean',label:'Afficher les étapes',default:true},
    showFinalReport:{type:'boolean',label:'Afficher le bilan',default:true},
    motherConcentrationMolL:n("Concentration de la solution mère","mol·L⁻¹",0.02,0.0001,5),
    targetConcentrationMolL:n("Concentration de la solution fille","mol·L⁻¹",0.006,0.00001,5),
    finalVolumeMl:n("Volume de la fiole","mL",50,10,1000,1),
    buretteResolutionMl:n("Résolution de la burette","mL",0.1,0.01,1,0.01),
    allowHints:{type:"boolean",label:"Autoriser les aides",default:true},
    maxMeasurements:{...n("Nombre maximal de préparations","",20,1,100,1),integer:true}
  },
  [spectroId]:{
    mode:{type:'string',label:'Mode de l’animation',default:'guided',enum:['method','guided','practice']},
    allowModeSwitch:{type:'boolean',label:'Autoriser les élèves à changer de mode',default:false},
    wavelengthMinNm:n("Longueur d’onde minimale","nm",400,350,900,1),
    wavelengthMaxNm:n("Longueur d’onde maximale","nm",900,400,1000,1),
    initialWavelengthNm:n("Longueur d’onde initiale","nm",810,350,1000,1),
    lambdaMaxNm:n("λmax du modèle","nm",810,350,1000,1),
    slopeAtLambdaMax:n("Pente A=f(C) à λmax","L·mol⁻¹",120,1,1000,1),
    noiseAmplitude:n("Amplitude du bruit déterministe","A",0.003,0,0.05,0.001),
    unknownConcentrationMolL:n("Concentration de l’inconnue diluée","mol·L⁻¹",0.0064,0.00001,1),
    maxMeasurements:{...n("Nombre maximal de mesures","",30,1,100,1),integer:true}
  }
};

export const chemistryDefaults=(id:string):Config=>Object.fromEntries(
  Object.entries(chemistrySchemas[id]??{}).map(([k,p])=>[k,structuredClone(p.default)])
);

// V3 puts the pipettes and flasks on the bench for the student to choose.
export const dilutionV3Schema:Record<string,Parameter>={
  ...Object.fromEntries(Object.entries(chemistrySchemas[dilutionId]).filter(([key])=>!['samplingDevice','samplingVolumeMl','pipetteVolumeMl','buretteResolutionMl'].includes(key))),
  finalVolumeMl:{...chemistrySchemas[dilutionId].finalVolumeMl,label:'Volume de solution fille demandé',enum:[50,100,200,250]},
};
export const dilutionV3Defaults=():Config=>Object.fromEntries(Object.entries(dilutionV3Schema).map(([key,p])=>[key,structuredClone(p.default)]));

export const isChemistryExperiment=(id:string)=>chemistryIds.includes(id as any);

export function validateChemistryConfig(id:string,input:unknown,fromUrl=false){
  const config=chemistryDefaults(id),errors:string[]=[];
  if(!isChemistryExperiment(id)||!input||typeof input!=="object"||Array.isArray(input))return {config,errors:["Configuration invalide."]};
  for(const [key,raw] of Object.entries(input as Record<string,unknown>)){
    const p=chemistrySchemas[id][key]; if(!p){errors.push(`Paramètre inconnu : ${key}`);continue;}
    let value:any=raw;
    if(fromUrl&&typeof raw==="string") value=p.type==="number"?Number(raw):p.type==="boolean"?(raw==="true"?true:raw==="false"?false:raw):raw;
    const ok=typeof value===p.type&&(!p.enum||p.enum.includes(value))&&(p.type!=="number"||(Number.isFinite(value)&&value>=p.minimum!&&value<=p.maximum!&&(!p.integer||Number.isInteger(value))));
    if(!ok)errors.push(`Valeur invalide : ${p.label}`); else config[key]=value as any;
  }
  if(id===dilutionId){
    const c0=Number(config.motherConcentrationMolL),c1=Number(config.targetConcentrationMolL),v=Number(config.finalVolumeMl);
    if(!(c1<c0))errors.push("La solution fille doit être moins concentrée que la solution mère.");
    const v0=c1*v/c0;
    if(!(v0>0&&v0<v))errors.push("Le volume à prélever doit être compris entre 0 et le volume final.");
  }
  if(id===spectroId){
    const min=Number(config.wavelengthMinNm),max=Number(config.wavelengthMaxNm),initial=Number(config.initialWavelengthNm),lm=Number(config.lambdaMaxNm);
    if(!(min<max&&initial>=min&&initial<=max&&lm>=min&&lm<=max))errors.push("Longueurs d’onde incohérentes.");
  }
  return {config,errors};
}

export function expectedAliquotMl(c:Config){
  return Number(c.targetConcentrationMolL)*Number(c.finalVolumeMl)/Number(c.motherConcentrationMolL);
}

export function spectralFactor(wavelengthNm:number,c:Config){
  const lm=Number(c.lambdaMaxNm);
  const width=115;
  return Math.exp(-0.5*Math.pow((wavelengthNm-lm)/width,2));
}
export function theoreticalAbsorbance(concentrationMolL:number,wavelengthNm:number,c:Config){
  return Number(c.slopeAtLambdaMax)*concentrationMolL*spectralFactor(wavelengthNm,c);
}
function noise(key:string,amp:number){
  let h=2166136261;
  for(const ch of key){h^=ch.charCodeAt(0);h=Math.imul(h,16777619);}
  return (((h>>>0)%2001)/1000-1)*amp;
}
export function measuredAbsorbance(solutionId:string,concentrationMolL:number,wavelengthNm:number,c:Config){
  return Math.max(0,theoreticalAbsorbance(concentrationMolL,wavelengthNm,c)+noise(`${solutionId}:${wavelengthNm}`,Number(c.noiseAmplitude)));
}

export function validChemistryMeasurement(id:string,m:any,c:Config){
  if(!m||typeof m!=="object"||typeof m.measurementId!=="string"||!Number.isInteger(m.measurementIndex)||m.measurementIndex<1)return false;
  if(id===dilutionId){
    return Number.isFinite(m.aliquotVolumeMl)&&Number.isFinite(m.finalVolumeMl)&&Number.isFinite(m.motherConcentrationMolL)&&
      Number.isFinite(m.preparedConcentrationMolL)&&typeof m.homogenized==="boolean"&&typeof m.overfilled==="boolean"&&m.unit?.aliquotVolumeMl==="mL";
  }
  if(id===spectroId){
    const known:Record<string,number>={S1:.002,S2:.004,S3:.006,S4:.008,S5:.01};
    return (m.solutionId==='unknown'?m.concentrationMolL===null:Object.hasOwn(known,m.solutionId)&&m.concentrationMolL===known[m.solutionId])&&Number.isFinite(m.wavelengthNm)&&Number.isFinite(m.absorbance)&&m.absorbance>=0&&
      m.wavelengthNm>=Number(c.wavelengthMinNm)&&m.wavelengthNm<=Number(c.wavelengthMaxNm)&&m.blanked===true&&m.unit?.wavelengthNm==="nm";
  }
  return false;
}
